import classNames from 'clsx';
import React, { memo, ReactElement, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useBuyState } from 'reactApp/components/VkBuyModalWithSidebar/hooks/useBuyState';
import { useContent } from 'reactApp/components/VkBuyModalWithSidebar/hooks/useContent';
import { useModalCallbacks } from 'reactApp/components/VkBuyModalWithSidebar/hooks/useModalCallbacks';
import { usePaymentCallbacks } from 'reactApp/components/VkBuyModalWithSidebar/hooks/usePaymentCallbacks';
import { EPaymentModalType, IProps } from 'reactApp/components/VkBuyModalWithSidebar/VkBuyModalWithSidebar.types';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { RootState } from 'reactApp/store';
import { Tariff } from 'reactApp/types/Billing';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { DMR } from 'reactApp/ui/DMRFrame/DMR';

import styles from './VkBuyModalWithSidebar.css';

export const VkBuyModalWithSidebar = memo(
    ({ productId, onClose, type, onSuccess, sidebarProps, tariff, isMarketingPromo }: IProps): ReactElement => {
        const INITIAL_HEIGHT = type === EPaymentModalType.fastcheckout ? 560 : 490; // Начальная высота окна оплаты

        const [height, setHeight] = useState<number>(INITIAL_HEIGHT);

        const { isLoading, hasError, link } = useBuyState({ productId, type });
        const { buySubscription, resetLink } = usePaymentCallbacks({ type });

        const [currentProductId, setProductId] = useState<string>(productId);

        const product = useSelector((state: RootState) => ProductsSelectors.getProductById(state, currentProductId));
        const storeTariff = useSelector((state: RootState) => ProductsSelectors.getTariffByProductId(state, product.id)) as Tariff;

        const { onSuccessCallback, onCloseModal, onCancel, onDmrFormSend, onShow, onClickTariffs } = useModalCallbacks({
            onClose,
            onSuccess,
            product,
            type,
        });

        const withFooter = type === EPaymentModalType.fastcheckout;

        const changeProduct = useCallback((id: string) => {
            setProductId(id);
        }, []);

        const handleDmrResize = useCallback(
            (params) => setHeight((params?.height > INITIAL_HEIGHT && params.height + (withFooter ? 70 : 0)) || INITIAL_HEIGHT),
            [withFooter]
        );

        const { sidebar, footer, header } = useContent({
            type,
            product,
            tariff: tariff || storeTariff,
            changeProduct,
            onClickTariffs,
            sidebarProps,
            isMarketingPromo,
        });

        useEffect(() => {
            onShow();
        }, []);

        return (
            <Dialog
                id="vk-buy-with-sidebar"
                open
                mod="frame"
                className={classNames({
                    [styles[`root_${type}`]]: !!type,
                })}
                onCancel={onCloseModal}
                closeOnDimmerClick={false}
                header={header}
            >
                <div className={styles.content} style={{ height: `${height}px` }}>
                    <div className={styles.sidebar} style={{ minHeight: `${INITIAL_HEIGHT}px`, height: `${height}px` }}>
                        {sidebar}
                    </div>
                    <div className={styles.payment} style={{ height: `${height}px` }}>
                        <DMR
                            buySubscription={buySubscription}
                            resetSubscription={resetLink}
                            subscription={product}
                            isLoading={isLoading}
                            hasError={hasError}
                            checkoutLink={link}
                            onFormSend={onDmrFormSend}
                            onCancel={onCancel}
                            onSuccess={onSuccessCallback}
                            onResize={handleDmrResize}
                            qaId={`payment-vk-${type}`}
                            minHeight={INITIAL_HEIGHT}
                            className={styles.dmr}
                            size={type === EPaymentModalType.fastcheckout ? 'larger' : null}
                        />
                        {footer}
                    </div>
                </div>
            </Dialog>
        );
    }
);

VkBuyModalWithSidebar.displayName = 'VkBuyModalWithSidebar';
